import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Trophy,
  Calendar,
  ClipboardCheck,
  UserCheck,
  ChevronRight,
  Loader2,
  MapPin,
  AlertCircle,
} from 'lucide-react';
import { useAthleteAuth } from '@/contexts/AthleteAuthContext';
import { api } from '@/hooks/useApi';

interface EventInfo {
  id: string;
  name: string;
  nameEn?: string;
  startDate: string;
  endDate: string;
  location: string;
}

interface CompetitionItem {
  id: string;
  name: string;
  nameEn?: string;
  date: string;
  time: string;
  venue: string;
  status: string;
}

const STATUS_STYLES: Record<string, { bg: string; text: string; label: string; labelEn: string }> = {
  PENDING: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: '審核中', labelEn: 'Pending' },
  APPROVED: { bg: 'bg-green-100', text: 'text-green-800', label: '已核准', labelEn: 'Approved' },
  REJECTED: { bg: 'bg-red-100', text: 'text-red-800', label: '未通過', labelEn: 'Rejected' },
};

export default function AthleteDashboardPage() {
  const { athlete, token } = useAthleteAuth();
  const [eventInfo, setEventInfo] = useState<EventInfo | null>(null);
  const [competitions, setCompetitions] = useState<CompetitionItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) return;

    const headers = { Authorization: `Bearer ${token}` };

    Promise.allSettled([
      api.get('/me/event', { headers }),
      api.get('/me/competitions', { headers }),
    ])
      .then(([eventRes, compRes]) => {
        if (eventRes.status === 'fulfilled') {
          const data = eventRes.value.data?.data ?? eventRes.value.data;
          setEventInfo(data);
        }
        if (compRes.status === 'fulfilled') {
          const data = compRes.value.data?.data ?? compRes.value.data;
          setCompetitions(Array.isArray(data) ? data : []);
        }
      })
      .finally(() => setLoading(false));
  }, [token]);

  if (!athlete) return null;

  const status = STATUS_STYLES[athlete.registrationStatus] ?? STATUS_STYLES.PENDING;

  // Profile completeness check
  const profileFields = [
    { key: 'phone', label: '電話', labelEn: 'Phone', filled: !!athlete.phone },
    { key: 'email', label: '電子郵件', labelEn: 'Email', filled: !!athlete.email },
    { key: 'address', label: '地址', labelEn: 'Address', filled: !!athlete.address },
    { key: 'emergencyContact', label: '緊急聯絡人', labelEn: 'Emergency Contact', filled: !!athlete.emergencyContact },
    { key: 'emergencyPhone', label: '緊急聯絡電話', labelEn: 'Emergency Phone', filled: !!athlete.emergencyPhone },
    { key: 'photoUrl', label: '個人照片', labelEn: 'Photo', filled: !!athlete.photoUrl },
  ];
  const filledCount = profileFields.filter((f) => f.filled).length;
  const totalFields = profileFields.length;
  const completionPercent = Math.round((filledCount / totalFields) * 100);

  return (
    <div>
      {/* Welcome */}
      <div className="mb-8">
        <h1 className="text-2xl md:text-3xl font-bold text-[var(--text-primary)]">
          歡迎，{athlete.name}
        </h1>
        <p className="text-sm text-[var(--text-secondary)] mt-1">
          Welcome, {athlete.nameEn || athlete.name}
        </p>
        <p className="text-sm text-[var(--text-secondary)] mt-0.5">
          {athlete.township} &middot; {athlete.eventName}
        </p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-[var(--primary)]" />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Event Info Card */}
          <div className="bg-[var(--bg-card)] rounded-xl border border-[var(--border)] p-6 shadow-sm">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-[var(--primary)] text-white flex items-center justify-center">
                <Calendar className="w-5 h-5" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-[var(--text-primary)]">賽事資訊</h2>
                <p className="text-xs text-[var(--text-secondary)]">Event Info</p>
              </div>
            </div>
            {eventInfo ? (
              <div className="space-y-2 text-sm">
                <p className="font-medium text-[var(--text-primary)]">{eventInfo.name}</p>
                {eventInfo.nameEn && (
                  <p className="text-xs text-[var(--text-secondary)]">{eventInfo.nameEn}</p>
                )}
                <div className="flex items-center gap-2 text-[var(--text-secondary)]">
                  <Calendar className="w-4 h-4 shrink-0" />
                  <span>
                    {eventInfo.startDate} ~ {eventInfo.endDate}
                  </span>
                </div>
                <div className="flex items-center gap-2 text-[var(--text-secondary)]">
                  <MapPin className="w-4 h-4 shrink-0" />
                  <span>{eventInfo.location}</span>
                </div>
              </div>
            ) : (
              <p className="text-sm text-[var(--text-secondary)]">暫無資訊 / No info available</p>
            )}
            <Link
              to="/athlete/event"
              className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-[var(--primary)] hover:underline"
            >
              查看詳情 / View Details <ChevronRight className="w-4 h-4" />
            </Link>
          </div>

          {/* Registration Status Card */}
          <div className="bg-[var(--bg-card)] rounded-xl border border-[var(--border)] p-6 shadow-sm">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-[var(--primary)] text-white flex items-center justify-center">
                <ClipboardCheck className="w-5 h-5" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-[var(--text-primary)]">報名狀態</h2>
                <p className="text-xs text-[var(--text-secondary)]">Registration Status</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <span
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-semibold ${status.bg} ${status.text}`}
              >
                {athlete.registrationStatus === 'APPROVED' && (
                  <UserCheck className="w-4 h-4" />
                )}
                {athlete.registrationStatus === 'REJECTED' && (
                  <AlertCircle className="w-4 h-4" />
                )}
                {status.label}
                <span className="text-xs font-normal opacity-70">/ {status.labelEn}</span>
              </span>
            </div>
            <p className="mt-3 text-xs text-[var(--text-secondary)]">
              報名狀態由鄉區工作人員審核管理
              <br />
              Registration status is managed by township staff.
            </p>
          </div>

          {/* My Competitions Card */}
          <div className="bg-[var(--bg-card)] rounded-xl border border-[var(--border)] p-6 shadow-sm">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-[var(--primary)] text-white flex items-center justify-center">
                <Trophy className="w-5 h-5" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-[var(--text-primary)]">我的競賽項目</h2>
                <p className="text-xs text-[var(--text-secondary)]">My Competitions</p>
              </div>
            </div>
            {competitions.length > 0 ? (
              <ul className="space-y-2">
                {competitions.slice(0, 4).map((comp) => (
                  <li
                    key={comp.id}
                    className="flex items-center justify-between px-3 py-2 rounded-lg bg-[var(--bg-secondary)] text-sm"
                  >
                    <span className="font-medium text-[var(--text-primary)]">{comp.name}</span>
                    <span className="text-xs text-[var(--text-secondary)]">{comp.date}</span>
                  </li>
                ))}
                {competitions.length > 4 && (
                  <li className="text-xs text-[var(--text-secondary)] text-center pt-1">
                    +{competitions.length - 4} 項 / more
                  </li>
                )}
              </ul>
            ) : (
              <p className="text-sm text-[var(--text-secondary)]">
                尚未報名任何競賽項目
                <br />
                <span className="text-xs">No competitions registered yet.</span>
              </p>
            )}
            <Link
              to="/athlete/competitions"
              className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-[var(--primary)] hover:underline"
            >
              查看全部 / View All <ChevronRight className="w-4 h-4" />
            </Link>
          </div>

          {/* Profile Completion Card */}
          <div className="bg-[var(--bg-card)] rounded-xl border border-[var(--border)] p-6 shadow-sm">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-[var(--primary)] text-white flex items-center justify-center">
                <UserCheck className="w-5 h-5" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-[var(--text-primary)]">個人資料完整度</h2>
                <p className="text-xs text-[var(--text-secondary)]">Profile Completion</p>
              </div>
            </div>

            {/* Progress bar */}
            <div className="mb-4">
              <div className="flex items-center justify-between text-sm mb-1.5">
                <span className="text-[var(--text-secondary)]">
                  {filledCount}/{totalFields} 欄位已填寫
                </span>
                <span className="font-semibold text-[var(--text-primary)]">{completionPercent}%</span>
              </div>
              <div className="w-full h-2.5 bg-[var(--bg-secondary)] rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full bg-[var(--primary)] transition-all duration-500"
                  style={{ width: `${completionPercent}%` }}
                />
              </div>
            </div>

            {/* Missing fields */}
            <ul className="space-y-1.5">
              {profileFields.map((field) => (
                <li key={field.key} className="flex items-center gap-2 text-sm">
                  <div
                    className={`w-2 h-2 rounded-full ${
                      field.filled ? 'bg-green-500' : 'bg-gray-300'
                    }`}
                  />
                  <span
                    className={
                      field.filled
                        ? 'text-[var(--text-primary)]'
                        : 'text-[var(--text-secondary)]'
                    }
                  >
                    {field.label}
                    <span className="text-xs opacity-60"> / {field.labelEn}</span>
                  </span>
                </li>
              ))}
            </ul>

            <Link
              to="/athlete/profile"
              className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-[var(--primary)] hover:underline"
            >
              編輯資料 / Edit Profile <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
